import React, { useEffect } from 'react'
import AdminDashboardLayout from './AdminDashboardLayout'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getSingleCorporatesAPI } from '../../redux/slices/superAdminCorporateSlice'
import { base } from '../../constants/base'
import './ProfileViewPage.css'

const CorporateVIewPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = useSelector(state => state.SuperAdminCorporateSlice.loading);
    const singleCorporate = useSelector(state => state.SuperAdminCorporateSlice.singleCorporate);
    const param = useParams();
    const id = param?.id;

    const fetchSingleCorporate = async () => {
        await dispatch(getSingleCorporatesAPI(id));
    }

    useEffect(() => {
        fetchSingleCorporate();
    }, [id]);

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'status-active';
            case 'block':
            case 'blocked':
                return 'status-blocked';
            default:
                return 'status-pending';
        }
    };

    return (
        <AdminDashboardLayout>
            <div className="profile-view-page">
                {/* Page Header */}
                <div className="pv-header">
                    <button className="pv-back-btn" onClick={() => navigate('/admin/corporates')}>
                        <i className="fas fa-arrow-left"></i>
                        <span>Back to Corporates</span>
                    </button>
                    <h1 className="pv-title">Corporate Details</h1>
                </div>

                {loading ? (
                    <div className="pv-loading">
                        <div className="pv-loader"></div>
                    </div>
                ) : (
                    <div className="pv-content">
                        {/* Profile Card */}
                        <div className="pv-profile-card">
                            <div className="pv-profile-header">
                                <div className="pv-avatar-section">
                                    {singleCorporate?.images?.[0]?.path ? (
                                        <img
                                            src={`${base.imageUrl}${singleCorporate?.images?.[0]?.path}`}
                                            alt={singleCorporate?.name}
                                            className="pv-avatar"
                                        />
                                    ) : (
                                        <div className="pv-avatar-placeholder">
                                            <i className="fas fa-building"></i>
                                        </div>
                                    )}
                                </div>
                                <div className="pv-profile-info">
                                    <h2 className="pv-name">{singleCorporate?.name || '-'}</h2>
                                    <p className="pv-email">{singleCorporate?.email || '-'}</p>
                                    <div className="pv-badges">
                                        <span className="pv-role-badge">Corporate</span>
                                        {singleCorporate?.status && (
                                            <span className={`pv-status-badge ${getStatusClass(singleCorporate?.status)}`}>
                                                {singleCorporate?.status}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div className="pv-details-grid">
                            {/* Contact Information */}
                            <div className="pv-details-card">
                                <div className="pv-card-header">
                                    <i className="fas fa-address-book"></i>
                                    <h3>Contact Information</h3>
                                </div>
                                <div className="pv-card-body">
                                    {singleCorporate?.phone_no && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-phone"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Phone Number</span>
                                                <span className="pv-info-value">{singleCorporate?.phone_no}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleCorporate?.address?.address && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-location-dot"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Address</span>
                                                <span className="pv-info-value">{singleCorporate?.address?.address}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Business Information */}
                            <div className="pv-details-card">
                                <div className="pv-card-header">
                                    <i className="fas fa-briefcase"></i>
                                    <h3>Business Information</h3>
                                </div>
                                <div className="pv-card-body">
                                    {singleCorporate?.ein_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-building"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">EIN Number</span>
                                                <span className="pv-info-value">{singleCorporate?.ein_number}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleCorporate?.registration_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-address-book"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Registration Number</span>
                                                <span className="pv-info-value">{singleCorporate?.registration_number}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Identification */}
                            <div className="pv-details-card">
                                <div className="pv-card-header">
                                    <i className="fas fa-id-card"></i>
                                    <h3>Identification</h3>
                                </div>
                                <div className="pv-card-body">
                                    {singleCorporate?.tax_id_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-shield-halved"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Tax ID Number</span>
                                                <span className="pv-info-value">{singleCorporate?.tax_id_number}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleCorporate?.work_permit_id && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-hashtag"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Work Permit ID</span>
                                                <span className="pv-info-value">{singleCorporate?.work_permit_id}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleCorporate?.ss_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-address-card"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Social Security Number</span>
                                                <span className="pv-info-value">{singleCorporate?.ss_number}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Documents */}
                            {singleCorporate?.images?.[1]?.path && (
                                <div className="pv-details-card">
                                    <div className="pv-card-header">
                                        <i className="fas fa-folder-open"></i>
                                        <h3>Documents</h3>
                                    </div>
                                    <div className="pv-card-body">
                                        <div className="pv-documents-grid">
                                            <a
                                                href={`${base.imageUrl}${singleCorporate?.images?.[1]?.path}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="pv-document-item"
                                            >
                                                <div className="pv-doc-icon">
                                                    <i className="fas fa-file-pdf"></i>
                                                </div>
                                                <div className="pv-doc-info">
                                                    <span className="pv-doc-name">Other Document</span>
                                                    <span className="pv-doc-action">Click to view</span>
                                                </div>
                                                <i className="fas fa-external-link-alt pv-doc-link"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* About */}
                            {singleCorporate?.about && (
                                <div className="pv-details-card pv-full-width">
                                    <div className="pv-card-header">
                                        <i className="fas fa-info-circle"></i>
                                        <h3>About Company</h3>
                                    </div>
                                    <div className="pv-card-body">
                                        <p className="pv-about-text">{singleCorporate?.about}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </AdminDashboardLayout>
    )
}

export default CorporateVIewPage
